import type { Position } from '../../types';
import type { MessageCallbacks } from './types';

function upsertPosition(positionUpdate: Position, cb: MessageCallbacks) {
  cb.setPositions(prev => {
    const existingIndex = prev.findIndex(p => p.symbol === positionUpdate.symbol);
    if (existingIndex >= 0) {
      const updated = [...prev];
      updated[existingIndex] = positionUpdate;
      return updated;
    }
    if (positionUpdate.quantity !== 0) return [...prev, positionUpdate];
    return prev;
  });
  if (positionUpdate.symbol === cb.selectedSymbol) {
    cb.setCurrentPosition(positionUpdate.quantity);
  }
}

export function handlePositionUpdate(obj: any, cb: MessageCallbacks): boolean {
  if (!((obj?.kind === 'POSITION_UPDATE' || obj?.type === 'POSITION_UPDATE') &&
        (obj.secId || obj.symbol || obj.data?.secId || obj.data?.symbol))) return false;

  const payload = obj.data || obj;
  const secId = (payload.secId || payload.sec_id || obj.secId || obj.symbol || '').toUpperCase();
  const symbol = payload.symbol || payload.secId || secId;
  const pos = payload.position || payload;

  const positionUpdate: Position = {
    symbol: (symbol || secId).toString().toUpperCase(),
    quantity: Number(pos.quantity ?? pos.qty ?? 0),
    avgPrice: Number(pos.avgPrice ?? pos.avg_price ?? 0),
    currentPrice: Number(pos.currentPrice ?? pos.current_price ?? pos.lastPrice ?? pos.last_price ?? 0) || undefined,
    unrealizedPnL: Number(pos.unrealizedPnL ?? pos.unrealized_pnl ?? 0),
    realizedPnL: Number(pos.realizedPnL ?? pos.realized_pnl ?? 0),
    totalPnL: Number(pos.totalPnL ?? pos.total_pnl ?? ((pos.realizedPnL ?? pos.realized_pnl ?? 0) + (pos.unrealizedPnL ?? pos.unrealized_pnl ?? 0))) || undefined,
    exposure: Number(pos.exposure ?? 0) || undefined,
    returnPercent: Number(pos.returnPercent ?? pos.return_percent ?? 0) || undefined,
    lastUpdate: (() => {
      const ts = payload.timestamp ?? obj.timestamp;
      if (!ts) return new Date().toISOString();
      return typeof ts === 'string' ? ts : new Date(ts as number).toISOString();
    })(),
  };

  upsertPosition(positionUpdate, cb);
  return true;
}

export function handlePosition(obj: any, cb: MessageCallbacks): boolean {
  if (!((obj?.kind === 'POSITION' || obj?.type === 'POSITION') &&
        (obj.symbol || obj.secId || obj.data?.symbol || obj.data?.secId))) return false;

  const payload = obj.data || obj;
  const symbol = (payload.symbol || payload.secId || obj.symbol || obj.secId || '').toUpperCase();
  const unrealizedPnL = Number(payload.unrealizedPnL ?? payload.unrealized_pnl ?? 0);
  const realizedPnL = Number(payload.realizedPnL ?? payload.realized_pnl ?? 0);

  const positionUpdate: Position = {
    symbol,
    quantity: Number(payload.quantity ?? payload.qty ?? 0),
    avgPrice: Number(payload.avgPrice ?? payload.avg_price ?? 0),
    currentPrice: Number(payload.currentPrice ?? payload.current_price ?? payload.lastPrice ?? 0) || undefined,
    unrealizedPnL,
    realizedPnL,
    totalPnL: unrealizedPnL + realizedPnL,
    exposure: Number(payload.exposure ?? 0) || undefined,
    returnPercent: Number(payload.returnPercent ?? payload.return_percent ?? 0) || undefined,
    lastUpdate: (payload.timestamp && (typeof payload.timestamp === 'string'
      ? payload.timestamp
      : new Date(payload.timestamp).toISOString())) || new Date().toISOString(),
  };

  upsertPosition(positionUpdate, cb);
  return true;
}

export function handlePortfolioTotals(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'PORTFOLIO_TOTALS' || obj?.type === 'PORTFOLIO_TOTALS')) return false;

  const payload = obj.data || obj;
  const p = (payload.portfolio && typeof payload.portfolio === 'object') ? payload.portfolio : payload;
  const r = (payload.risk && typeof payload.risk === 'object') ? payload.risk : payload;

  cb.setPortfolio({
    totalUnrealizedPnL: Number(p.totalUnrealizedPnL ?? p.total_unrealized_pnl ?? 0),
    totalRealizedPnL: Number(p.totalRealizedPnL ?? p.total_realized_pnl ?? 0),
    totalPnL: Number(p.totalPnL ?? p.total_pnl ?? (Number(p.totalUnrealizedPnL ?? p.total_unrealized_pnl ?? 0) + Number(p.totalRealizedPnL ?? p.total_realized_pnl ?? 0))),
    totalExposure: Number(p.totalExposure ?? p.total_exposure ?? 0),
  });

  cb.setRisk({
    grossNotional: Number(r.grossNotional ?? r.gross_notional ?? 0),
    longExposure: Number(r.longExposure ?? r.long_exposure ?? 0),
    shortExposure: Number(r.shortExposure ?? r.short_exposure ?? 0),
    netExposure: Number(r.netExposure ?? r.net_exposure ?? 0),
    capitalUtilization: Number(r.capitalUtilization ?? r.capital_utilization ?? 0),
    priceSensitivity1pct: Number(r.priceSensitivity1pct ?? 0),
    priceSensitivity5pct: Number(r.priceSensitivity5pct ?? 0),
    realizedVolRisk: Number(r.realizedVolRisk ?? r.realized_vol_risk ?? 0),
    inventoryByTicker: r.inventoryByTicker ?? r.inventory_by_ticker ?? {},
  });
  return true;
}
